import React, { createContext, ReactElement, useCallback, useContext, useState } from "react";
import ConfirmationDialog from "./confirmation-dialog";
import { ButtonProps } from "../ui/button";

export interface ConfirmationDialogConfig {
    title: string | ReactElement;
    body: string | ReactElement;
    confirmButtonText?: string;
    confirmButtonVariant?: ButtonProps["variant"];
    cancelButtonText?: string;
}

interface DialogState {
    config: ConfirmationDialogConfig;
    action: () => void | Promise<void>;
}

interface ConfirmationDialogContextValue {
    confirm: (config: ConfirmationDialogConfig, action: () => void | Promise<void>) => () => void;
}

const ConfirmationDialogContext = createContext<ConfirmationDialogContextValue | undefined>(undefined);

export function ConfirmationDialogProvider({ children }: React.PropsWithChildren) {
    const [dialogState, setDialogState] = useState<DialogState | null>(null);

    const confirm = useCallback((config: ConfirmationDialogConfig, action: () => void | Promise<void>) => {
        return () => {
            setDialogState({ config, action });
        };
    }, []);

    const handleConfirm = async () => {
        if (dialogState) {
            await dialogState.action();
            setDialogState(null);
        }
    };

    const handleCancel = () => {
        setDialogState(null);
    };

    return (
        <ConfirmationDialogContext.Provider value={{ confirm }}>
            {children}
            {dialogState && (
                <ConfirmationDialog
                    {...dialogState.config}
                    open={true}
                    onClose={handleCancel}
                    confirm={handleConfirm}
                />
            )}
        </ConfirmationDialogContext.Provider>
    );
}

export function useConfirmDialog() {
    const ctx = useContext(ConfirmationDialogContext);
    if (!ctx) {
        throw new Error("useConfirmDialog must be used within ConfirmationDialogProvider");
    }
    return ctx.confirm;
}
